import { useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Id, Kurs, Knoten, Unterrichtsform, KursStatus } from '../daten/typen';
import { sollIst, kinderVon } from '../daten/regeln';
import { deLang, plusTage, montag, heute, wochentag } from '../daten/zeit';
import { Knopf, Feld, Fortschritt, Seitenpanel, Icon, Leer } from '../ui/bausteine';
import { MoodleBaustein } from './MoodleBaustein';
import { kursStundenplanDrucken } from '../daten/export';

export function Kurse({ zurPlanung }: { zurPlanung: (woche: string) => void }) {
  const { stand, melden } = useDaten();
  const [auswahl, setAuswahl] = useState<Id | null>(null);
  const [neu, setNeu] = useState(false);
  const [alle, setAlle] = useState(false);
  const liste = stand.kurse.filter(k => alle || k.status !== 'abgeschlossen').sort((a, b) => a.beginn.localeCompare(b.beginn));
  const k = stand.kurse.find(x => x.id === auswahl);

  return (
    <div className="seite">
      <header className="seiten-kopf zeile">
        <div><h1>Kurse</h1><p className="leise">Laufzeit, Ort, Lehrplan und Moodle-Anbindung je Kurs</p></div>
        <div className="zeile">
          <label className="check"><input type="checkbox" checked={alle} onChange={e => setAlle(e.target.checked)} /> abgeschlossene zeigen</label>
          <Knopf art="primaer" icon="plus" onClick={() => setNeu(true)}>Kurs anlegen</Knopf>
        </div>
      </header>
      {liste.length === 0 && <Leer icon="kalender" titel="Noch keine Kurse" aktion={<Knopf art="primaer" icon="plus" onClick={() => setNeu(true)}>Ersten Kurs anlegen</Knopf>} />}
      <div className="karten">
        {liste.map(kurs => {
          const si = sollIst(stand, kurs.id);
          const w = stand.kursKnoten.filter(x => x.kurs_id === kurs.id && !x.parent_id).reduce((s, x) => { const v = si.get(x.id)!; return { soll: s.soll + v.soll, g: s.g + v.geplant, f: s.f + v.freigegeben }; }, { soll: 0, g: 0, f: 0 });
          const st = stand.standorte.find(s => s.id === kurs.standort_id);
          return (
            <article key={kurs.id} className="karte kurs-karte" style={{ ['--kursfarbe' as any]: kurs.farbe }}>
              <div className="karte-kopf"><strong>{kurs.kennung}</strong><span className={`tag status-${kurs.status}`}>{kurs.status === 'planung' ? 'in Planung' : kurs.status === 'aktiv' ? 'läuft' : 'abgeschlossen'}</span></div>
              <div>{kurs.bezeichnung}</div>
              <div className="leise klein">{deLang(kurs.beginn)} – {deLang(kurs.ende)} · {st?.bezeichnung ?? 'ohne Standort'} · {kurs.standard_form === 'online' ? 'online' : 'Präsenz'}</div>
              <Fortschritt wert={w.g} max={w.soll} freigegeben={w.f} />
              <div className="klein">{w.g} von {w.soll} UE geplant · {w.f} freigegeben</div>
              <div className="knopfleiste">
                <Knopf icon="kalender" onClick={() => zurPlanung(montag(kurs.beginn > heute() ? kurs.beginn : heute()))}>Planen</Knopf>
                <Knopf art="leise" icon="stift" onClick={() => setAuswahl(kurs.id)}>Einstellungen und Lehrplan</Knopf>
                <Knopf art="leise" icon="download" onClick={() => { if (!kursStundenplanDrucken(stand, kurs.id)) melden('Die Druckansicht wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben.', 'info'); }}>Stundenplan als PDF</Knopf>
              </div>
            </article>
          );
        })}
      </div>
      <Seitenpanel offen={neu} schliessen={() => setNeu(false)} titel="Kurs anlegen" untertitel="Der Lehrplan wird aus der Vorlage übernommen und kann danach angepasst werden.">
        {neu && <NeuerKursFormular schliessen={() => setNeu(false)} geoeffnet={id => { setNeu(false); setAuswahl(id); }} />}
      </Seitenpanel>
      <Seitenpanel offen={!!k} schliessen={() => setAuswahl(null)} titel={k?.kennung ?? ''} untertitel={k?.bezeichnung}>
        {k && <KursDetail key={k.id} kurs={k} schliessen={() => setAuswahl(null)} />}
      </Seitenpanel>
    </div>
  );
}

const KURSFARBEN = ['#073070', '#2F6FB8', '#0E7C66', '#5B4A8A', '#9A5B00', '#3F5566', '#7A3E6B', '#4C6A1E'];

function NeuerKursFormular({ schliessen, geoeffnet }: { schliessen: () => void; geoeffnet: (id: Id) => void }) {
  const { stand, quelle, ausfuehren } = useDaten();
  const vorlage = stand.vorlagen[0];
  const naechsterMontag = plusTage(montag(heute()), 7);
  const [w, setW] = useState({
    vorlage_id: vorlage?.id ?? '', bezeichnung: 'Teile III und IV der Meisterprüfung', kennung: '',
    beginn: naechsterMontag, ende: plusTage(naechsterMontag, (vorlage?.standard_wochen ?? 7) * 7 - 3),
    standort_id: stand.standorte[0]?.id ?? null as Id | null, standard_raum_id: null as Id | null,
    zeitraster_id: stand.zeitraster[0]?.id ?? null as Id | null, standard_form: 'praesenz' as Unterrichtsform,
    farbe: KURSFARBEN[stand.kurse.length % KURSFARBEN.length],
  });
  const set = (k: string, v: any) => setW(x => ({ ...x, [k]: v }));
  const v = stand.vorlagen.find(x => x.id === w.vorlage_id);
  const sollGesamt = stand.vorlageKnoten.filter(x => x.vorlage_id === w.vorlage_id && !x.parent_id).reduce((s, x) => s + (x.soll_ue ?? 0), 0);
  // Unterrichtstage im Zeitraum (ohne Wochenenden und Feiertage)
  let tage = 0;
  for (let d = w.beginn; d <= w.ende; d = plusTage(d, 1)) if (wochentag(d) < 5 && !stand.kalender.some(t => t.datum === d && (!t.standort_id || t.standort_id === w.standort_id))) tage++;
  const fensterProTag = stand.zeitfenster.filter(z => z.zeitraster_id === w.zeitraster_id).length;
  const kapazitaet = tage * fensterProTag * 4;

  return (
    <form className="formular" onSubmit={async e => {
      e.preventDefault();
      let id = '';
      if (await ausfuehren(async () => { id = await quelle.kursAnlegen(w); }, 'Kurs angelegt')) geoeffnet(id);
    }}>
      <Feld label="Vorlage"><select value={w.vorlage_id} onChange={e => set('vorlage_id', e.target.value)}>{stand.vorlagen.map(x => <option key={x.id} value={x.id}>{x.bezeichnung}</option>)}</select></Feld>
      <div className="raster-2">
        <Feld label="Kennung"><input required value={w.kennung} onChange={e => set('kennung', e.target.value)} placeholder="z. B. ML-99-VZ-236" /></Feld>
        <Feld label="Farbe"><input type="color" value={w.farbe} onChange={e => set('farbe', e.target.value)} /></Feld>
      </div>
      <Feld label="Bezeichnung"><input required value={w.bezeichnung} onChange={e => set('bezeichnung', e.target.value)} /></Feld>
      <div className="raster-2">
        <Feld label="Beginn"><input type="date" required value={w.beginn} onChange={e => { set('beginn', e.target.value); set('ende', plusTage(e.target.value, (v?.standard_wochen ?? 7) * 7 - 3)); }} /></Feld>
        <Feld label="Ende"><input type="date" required value={w.ende} min={w.beginn} onChange={e => set('ende', e.target.value)} /></Feld>
        <Feld label="Standort"><select value={w.standort_id ?? ''} onChange={e => { set('standort_id', e.target.value || null); set('standard_raum_id', null); }}>
          <option value="">– keiner –</option>{stand.standorte.map(s => <option key={s.id} value={s.id}>{s.bezeichnung}</option>)}</select></Feld>
        <Feld label="Standardraum"><select value={w.standard_raum_id ?? ''} onChange={e => set('standard_raum_id', e.target.value || null)} disabled={w.standard_form === 'online'}>
          <option value="">– keiner –</option>{stand.raeume.filter(r => r.standort_id === w.standort_id).map(r => <option key={r.id} value={r.id}>{r.bezeichnung}</option>)}</select></Feld>
        <Feld label="Zeitraster"><select value={w.zeitraster_id ?? ''} onChange={e => set('zeitraster_id', e.target.value || null)}>
          {stand.zeitraster.map(z => <option key={z.id} value={z.id}>{z.bezeichnung}</option>)}</select></Feld>
        <Feld label="Unterrichtsform"><select value={w.standard_form} onChange={e => set('standard_form', e.target.value)}><option value="praesenz">Präsenz</option><option value="online">Online</option></select></Feld>
      </div>
      <div className={`hinweisbox ${kapazitaet < sollGesamt ? 'warn' : 'info'}`}>
        <Icon name={kapazitaet < sollGesamt ? 'warnung' : 'kalender'} />
        {tage} Unterrichtstage × {fensterProTag} Zeitfenster = {kapazitaet} UE möglich; der Lehrplan sieht {sollGesamt} UE vor.
        {kapazitaet < sollGesamt ? ` Es fehlen ${sollGesamt - kapazitaet} UE (${Math.ceil((sollGesamt - kapazitaet) / 4)} Einsätze) – z. B. Abendtermine, Samstage oder längere Laufzeit.` : ''}
      </div>
      <div className="knopfleiste"><Knopf art="primaer" icon="haken" type="submit">Kurs anlegen</Knopf><Knopf art="leise" onClick={schliessen}>Abbrechen</Knopf></div>
    </form>
  );
}

function KursDetail({ kurs, schliessen }: { kurs: Kurs; schliessen: () => void }) {
  const [reiter, setReiter] = useState<'lehrplan' | 'einstellungen' | 'moodle'>('lehrplan');
  return (
    <div>
      <div className="reiter" role="tablist">
        {(['lehrplan', 'einstellungen', 'moodle'] as const).map(r => (
          <button key={r} role="tab" aria-selected={reiter === r} className={reiter === r ? 'an' : ''} onClick={() => setReiter(r)}>
            {r === 'lehrplan' ? 'Lehrplan' : r === 'einstellungen' ? 'Einstellungen' : 'Moodle'}</button>
        ))}
      </div>
      {reiter === 'lehrplan' && <LehrplanEditor tabelle="kurs_knoten" elternFeld={{ kurs_id: kurs.id }} />}
      {reiter === 'einstellungen' && <KursEinstellungen kurs={kurs} schliessen={schliessen} />}
      {reiter === 'moodle' && <MoodleBaustein kurs={kurs} />}
    </div>
  );
}

function KursEinstellungen({ kurs, schliessen }: { kurs: Kurs; schliessen: () => void }) {
  const { stand, quelle, ausfuehren, fragen } = useDaten();
  const [w, setW] = useState<Kurs>(kurs);
  const set = (k: keyof Kurs, v: any) => setW(x => ({ ...x, [k]: v }));
  return (
    <form className="formular" onSubmit={async e => { e.preventDefault();
      const { moodle_token: _t, ...rest } = w; await ausfuehren(() => quelle.speichern('kurs', rest), 'Kurs gespeichert'); }}>
      <div className="raster-2">
        <Feld label="Kennung"><input required value={w.kennung} onChange={e => set('kennung', e.target.value)} /></Feld>
        <Feld label="Farbe"><input type="color" value={w.farbe} onChange={e => set('farbe', e.target.value)} /></Feld>
      </div>
      <Feld label="Bezeichnung"><input required value={w.bezeichnung} onChange={e => set('bezeichnung', e.target.value)} /></Feld>
      <div className="raster-2">
        <Feld label="Beginn"><input type="date" value={w.beginn} onChange={e => set('beginn', e.target.value)} /></Feld>
        <Feld label="Ende"><input type="date" value={w.ende} onChange={e => set('ende', e.target.value)} /></Feld>
        <Feld label="Standort"><select value={w.standort_id ?? ''} onChange={e => set('standort_id', e.target.value || null)}><option value="">– keiner –</option>{stand.standorte.map(s => <option key={s.id} value={s.id}>{s.bezeichnung}</option>)}</select></Feld>
        <Feld label="Standardraum"><select value={w.standard_raum_id ?? ''} onChange={e => set('standard_raum_id', e.target.value || null)}><option value="">– keiner –</option>{stand.raeume.filter(r => r.standort_id === w.standort_id).map(r => <option key={r.id} value={r.id}>{r.bezeichnung}</option>)}</select></Feld>
        <Feld label="Zeitraster"><select value={w.zeitraster_id ?? ''} onChange={e => set('zeitraster_id', e.target.value || null)}>{stand.zeitraster.map(z => <option key={z.id} value={z.id}>{z.bezeichnung}</option>)}</select></Feld>
        <Feld label="Unterrichtsform"><select value={w.standard_form} onChange={e => set('standard_form', e.target.value as Unterrichtsform)}><option value="praesenz">Präsenz</option><option value="online">Online</option></select></Feld>
        <Feld label="Status"><select value={w.status} onChange={e => set('status', e.target.value as KursStatus)}><option value="planung">in Planung</option><option value="aktiv">läuft</option><option value="abgeschlossen">abgeschlossen</option></select></Feld>
      </div>
      <div className="knopfleiste">
        <Knopf art="primaer" icon="haken" type="submit">Speichern</Knopf>
        <Knopf art="gefahr" icon="muell" onClick={async () => {
          if (!await fragen(`Kurs ${kurs.kennung} mit allen Terminen endgültig löschen?`, 'Kurs löschen', true)) return;
          if (await ausfuehren(() => quelle.loeschen('kurs', kurs.id), 'Kurs gelöscht')) schliessen();
        }}>Kurs löschen</Knopf>
      </div>
    </form>
  );
}

/** Baum-Editor für Lehrpläne (Kurs) und Vorlagen */
export function LehrplanEditor({ tabelle, elternFeld }: { tabelle: 'kurs_knoten' | 'vorlage_knoten'; elternFeld: { kurs_id: Id } | { vorlage_id: Id } }) {
  const { stand, quelle, ausfuehren, fragen } = useDaten();
  const alle = (tabelle === 'kurs_knoten' ? stand.kursKnoten : stand.vorlageKnoten) as (Knoten & Record<string, any>)[];
  const [schluessel, wert] = Object.entries(elternFeld)[0];
  const knoten = alle.filter(k => k[schluessel] === wert);
  const kinder = kinderVon(knoten as any);
  const si = tabelle === 'kurs_knoten' ? sollIst(stand, wert) : null;
  const neu = (parent: Id | null) => ausfuehren(() => quelle.speichern(tabelle, {
    ...elternFeld, parent_id: parent, bezeichnung: 'Neues Thema', kuerzel: null, fachbereich_id: null, soll_ue: 4,
    sortierung: (kinder.get(parent)?.length ?? 0),
  }));
  const summe = (kinder.get(null) ?? []).reduce((s, k) => s + (k.soll_ue ?? 0), 0);

  const Zeile = ({ k, tiefe }: { k: Knoten; tiefe: number }) => {
    const kinderSumme = (kinder.get(k.id) ?? []).reduce((s, c) => s + (c.soll_ue ?? 0), 0);
    const abweichung = kinder.has(k.id) && kinderSumme !== (k.soll_ue ?? 0);
    const w = si?.get(k.id);
    return (
      <>
        <div className="lp-zeile" style={{ paddingLeft: 6 + tiefe * 18 }}>
          <input className="lp-name" defaultValue={k.bezeichnung} aria-label="Bezeichnung"
            onBlur={e => e.target.value !== k.bezeichnung && ausfuehren(() => quelle.speichern(tabelle, { id: k.id, bezeichnung: e.target.value }))} />
          <select className="kompakt" value={k.fachbereich_id ?? ''} aria-label="Fachbereich"
            onChange={e => ausfuehren(() => quelle.speichern(tabelle, { id: k.id, fachbereich_id: e.target.value || null }))}>
            <option value="">{tiefe === 0 ? '–' : 'erbt'}</option>
            {stand.fachbereiche.map(f => <option key={f.id} value={f.id}>{f.kuerzel}</option>)}
          </select>
          <input className="lp-ue" type="number" min={0} step={1} defaultValue={k.soll_ue ?? ''} aria-label="Soll-UE"
            onBlur={e => Number(e.target.value) !== k.soll_ue && ausfuehren(() => quelle.speichern(tabelle, { id: k.id, soll_ue: e.target.value === '' ? null : Number(e.target.value) }))} />
          <span className={`lp-info klein ${abweichung ? 'warn-text' : 'leise'}`} title={abweichung ? `Summe der Unterpunkte: ${kinderSumme} UE` : ''}>
            {w ? `${w.geplant} gepl.` : ''}{abweichung ? ` · Σ ${kinderSumme}` : ''}</span>
          <Knopf art="leise" icon="plus" aria-label="Unterpunkt hinzufügen" onClick={() => neu(k.id)} />
          <Knopf art="leise" icon="muell" aria-label="Löschen" onClick={async () => { if (await fragen(`„${k.bezeichnung}“ mit allen Unterpunkten löschen?`, 'Löschen', true)) await ausfuehren(() => quelle.loeschen(tabelle, k.id)); }} />
        </div>
        {(kinder.get(k.id) ?? []).map(c => <Zeile key={c.id} k={c} tiefe={tiefe + 1} />)}
      </>
    );
  };

  return (
    <div className="lehrplan">
      <div className="lp-kopf klein leise"><span>Thema</span><span>FB</span><span>Soll-UE</span><span /></div>
      {(kinder.get(null) ?? []).map(k => <Zeile key={k.id} k={k} tiefe={0} />)}
      <div className="zeile"><Knopf icon="plus" onClick={() => neu(null)}>Hauptpunkt hinzufügen</Knopf><span className="leise klein">Summe: {summe} UE · ein Einsatz = 4 UE</span></div>
    </div>
  );
}
